/**
 * Combined campaigns hook — Voluum and Binom in parallel, plus Tres Commas
 * wherever this deployment enables it (useEnabledTrackers). Each row carries
 * `provider`, so the Campaigns page routes to its tracker and the tag-rule
 * pickers key it by tracker (a Tres Commas id can equal a Voluum id).
 * Tres Commas not enabled: exactly the Voluum + Binom answer (the same array)
 * and /tres-commas is never asked. Enabled: the Voluum + Binom rows never wait
 * for it — `pendingTrackers` says it is still on its way — and a failing
 * Tres Commas read leaves the Voluum + Binom list as it is.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import type { TrackerId } from '@trafficpulse/shared';

import { useEnabledTrackers } from '@/features/trackers/useEnabledTrackers';
import { mergeTresCommasCampaigns } from '@/hooks/campaign-tracker/merge-tres-commas';
import { idsByTracker, pickTracker } from '@/hooks/campaign-tracker/tracker-dispatch';
import { useTresCommasCampaign, useTresCommasCampaignFlows } from '@/hooks/tres-commas/useTresCommasCampaign';
import { useTresCommasCampaigns } from '@/hooks/tres-commas/useTresCommasCampaigns';

import {
  useCampaigns as useVoluumCampaigns,
  useCampaign as useVoluumCampaign,
  useCampaignFlows as useVoluumCampaignFlows,
  type Campaign,
  type CampaignDetail,
  type CampaignProvider,
  type Flow,
} from './voluum/useVoluum';
import {
  useBinomCampaign,
  useBinomCampaignFlows,
  useBinomCampaigns,
} from './binom';

export interface AllCampaignsList {
  data: Campaign[];
  /** Voluum + Binom still loading, or nothing listed yet while Tres Commas loads. */
  isLoading: boolean;
  /** Trackers whose campaigns are still on their way while the others are listed. */
  pendingTrackers: readonly TrackerId[];
  error: unknown;
}

const NONE: readonly TrackerId[] = [];
const TRES_COMMAS_PENDING: readonly TrackerId[] = ['tres-commas'];

function useVoluumBinomCampaigns() {
  const voluum = useVoluumCampaigns();
  const binom = useBinomCampaigns();
  const data = useMemo(() => {
    const merged: Campaign[] = [
      ...(voluum.data ?? []),
      ...(binom.data ?? []),
    ];
    return [...merged].sort((a, b) => a.name.localeCompare(b.name));
  }, [voluum.data, binom.data]);
  return {
    data,
    isLoading: voluum.isLoading || binom.isLoading,
    error: voluum.error ?? binom.error,
  };
}

export function useAllCampaigns(): AllCampaignsList {
  const base = useVoluumBinomCampaigns();
  const withTresCommas = useEnabledTrackers().includes('tres-commas');
  const tresCommas = useTresCommasCampaigns(withTresCommas);
  const rows = withTresCommas ? tresCommas.data : undefined;
  const data = useMemo(() => mergeTresCommasCampaigns(base.data, rows), [base.data, rows]);
  const tresCommasPending = withTresCommas && tresCommas.isLoading;
  const isLoading = base.isLoading || (tresCommasPending && data.length === 0);
  return {
    data,
    isLoading,
    pendingTrackers: tresCommasPending && !isLoading ? TRES_COMMAS_PENDING : NONE,
    error: base.error ?? (withTresCommas ? tresCommas.error : null),
  };
}

interface CampaignByProviderResult {
  data: CampaignDetail | undefined;
  isLoading: boolean;
  error: unknown;
}

/** Only the hook of the tracker `provider` names gets the id; the others stay off. */
export function useCampaignByProvider(
  campaignId: string,
  provider: CampaignProvider,
): CampaignByProviderResult {
  const ids = idsByTracker(provider, campaignId);
  const voluum = useVoluumCampaign(ids.voluum);
  const binom = useBinomCampaign(ids.binom);
  const tresCommas = useTresCommasCampaign(ids['tres-commas']);
  return pickTracker<CampaignByProviderResult>(provider, {
    voluum: { data: voluum.data, isLoading: voluum.isLoading, error: voluum.error },
    binom: { data: binom.data, isLoading: binom.isLoading, error: binom.error },
    'tres-commas': {
      data: tresCommas.data ?? undefined,
      isLoading: tresCommas.isLoading,
      error: tresCommas.error,
    },
  });
}

interface CampaignFlowsByProviderResult {
  data: Flow[] | undefined;
  isLoading: boolean;
  error: unknown;
}

export function useCampaignFlowsByProvider(
  campaignId: string,
  provider: CampaignProvider,
): CampaignFlowsByProviderResult {
  const ids = idsByTracker(provider, campaignId);
  const voluum = useVoluumCampaignFlows(ids.voluum);
  const binom = useBinomCampaignFlows(ids.binom);
  const tresCommas = useTresCommasCampaignFlows(ids['tres-commas']);
  return pickTracker<CampaignFlowsByProviderResult>(provider, {
    voluum: { data: voluum.data, isLoading: voluum.isLoading, error: voluum.error },
    binom: { data: binom.data, isLoading: binom.isLoading, error: binom.error },
    'tres-commas': {
      data: tresCommas.data,
      isLoading: tresCommas.isLoading,
      error: tresCommas.error,
    },
  });
}
